import { useLocalStorage } from '@vueuse/core';
import { FetchError } from 'ofetch';
import { captureMessage } from '@sentry/vue';

import { http, useAPI, StatusCode, objToFormData, isFetchError } from '~~core';

import { CreateFacematchInfoSchema, faceMatchConfigSchema, ocrSchema } from '../schemas/facial-biometrics';

function checkForceReloadPage(error: FetchError) {
  if (error.status === StatusCode.CONFLICT) {
    reloadNuxtApp();
    useLocalStorage('facematchData', null).value = null;
    captureMessage('Facematch data has been reset due to conflict error');
  }
}

export async function appendFacematchPhoto(signatureRequestKey: string, facematchKey: string, image: Blob, type: string, position: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const photo = { image, type, position };
  const bodyData = objToFormData({ photo });

  try {
    await http(getEndpoint(`facematches/${facematchKey}/append_photo`), {
      method: 'post',
      body: bodyData,
    });
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }
}

type AppendFacematchEvidence = {
  blob?: Blob;
  livenessResponse: string;
};

export async function appendFacematchSelfie(facematchKey: string, signatureRequestKey: string, { blob = undefined, livenessResponse }: AppendFacematchEvidence) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  const selfie = {
    image: blob || null,
    livenessResponse,
  };
  const bodyData = objToFormData({ selfie });

  try {
    await http(getEndpoint(`facematches/${facematchKey}/append_selfie`), {
      method: 'post',
      body: bodyData,
    });
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }
}

export async function fetchFacematchStatus(signatureRequestKey: string, facematchKey: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  try {
    await http(getEndpoint(`facematches/${facematchKey}/check`));
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }
}

export async function createFacematch(signatureRequestKey: string, documentType: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  let data;

  try {
    data = await http(getEndpoint('facematches'), {
      method: 'post',
      body: { documentType },
    });
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }

  return CreateFacematchInfoSchema.parse(data);
}

export async function fetchFacematchInfo(signatureRequestKey: string, type: string, facematchKey?: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  let data;
  const documentType = type.toLowerCase();

  try {
    data = await http(getEndpoint(`facematches/${facematchKey}`));
  } catch (error) {
    if (!isFetchError(error)) throw error;
    if (error.status !== StatusCode.NOT_FOUND) throw error;

    checkForceReloadPage(error);

    data = await createFacematch(signatureRequestKey, documentType);
  }

  return CreateFacematchInfoSchema.parse(data).facematch;
}

export async function fetchFaceMatchesConfig(signatureRequestKey: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  let data;

  try {
    data = await http(getEndpoint('facematches/config'));
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }

  return faceMatchConfigSchema.parse(data);
}

export async function checkOcr(signatureRequestKey: string, recognizableType: string, recognizableKey: string, signerDocumentation: string) {
  const { getEndpoint } = useAPI(signatureRequestKey);

  let data;

  try {
    data = await http(getEndpoint('ocrs'), {
      method: 'POST',
      body: {
        ocr: {
          recognizableType,
          recognizableKey,
          signerDocumentation,
        },
      },
    });
  } catch (error) {
    if (isFetchError(error)) checkForceReloadPage(error);

    throw error;
  }

  return ocrSchema.parse(data);
}
